/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';

export type TrainingScreenProps = {
    /**
     * The name of the currently active version.
     */
    versionName: string;

    /**
     * Indicates if training is needed for the
     * current active version.
     */
    needsTraining: boolean;

    /**
     * A callback fires when the training checkbox changes.
     */
    onChange: (needsTraining: boolean) => void;
};

const messages = defineMessages({
    description: {
        id: 'entityMigration.trainingScreen.description',
        defaultMessage: 'The active version, {versionName}, has untrained changes. Would you like to continue?'
    },
    trainLabel: {
        id: 'entityMigration.trainingScreen.trainLabel',
        defaultMessage: 'Train version {versionName} now'
    }
});

/**
 * Displays the optional training screen.
 */
export const TrainingScreen = (props: TrainingScreenProps) => {
    const { versionName, needsTraining, onChange } = props;
    const { localize } = useLocalization();

    return (
        <Stack tokens={{ childrenGap: 25 }} styles={{ root: { minHeight: '380px' } }}>
            <LuisText>{localize(messages.description, { versionName })}</LuisText>

            <Checkbox
                checked={needsTraining}
                onChange={(_, checked) => onChange(checked)}
                label={localize(messages.trainLabel, { versionName })}
            />
        </Stack>
    );
};
